import Finder from '../../../common/helpers/finder';
import { printGuideLog, summarizePathsForLog } from '../helpers/guideLogHelper';
import Overlay from './Overlay';
import Container from './Container';
import { NextStepButtonType } from '../../demo/dto/FrameTooltipDTO';
import { MessageTypes } from '../types/MessageTypes';
import ClickCapture from './ClickCapture';
import { isGuideRecoveryDisabled } from '../helpers/guideRecoveryHelper';
import {
  GUIDE_SCROLL_MARGIN_PX,
  isGuideTargetUncomfortable,
  resolveGuideScrollMargin,
  scrollElementIntoViewForGuide
} from '../helpers/guideScrollHelper';

class Guide {
  private static readonly RECOVERY_THROTTLE_MS = 300;

  private currentElement: HTMLElement | null = null;
  private paths: {
    xpath: string;
    cssPath: string[];
    textXPath: string | null;
    shadowRoot?: boolean;
    iframe?: boolean;
  }[] = [];
  private positionAnimationId: number | null = null;
  private lastPosition: { x: number; y: number; width: number; height: number } | null = null;
  private lastVisibility: boolean | null = null;
  private messageListener: ((event: MessageEvent) => void) | null = null;
  private nextStepButtonType: NextStepButtonType | null = null;
  private shouldDimBackground = true;
  private shouldShowHighlight = false;
  private clickedElement: HTMLElement | null = null;
  private scrollLock = false;
  private lastScrollSignature: string | null = null;
  private lastScrollAt = 0;
  private scrollReleaseTimeoutId: number | null = null;
  private scrollRequestToken = 0;
  /** Bumped so in-flight sendElementDimensions cannot paint stale rects after a newer measure. */
  private dimensionsApplyToken = 0;
  private lastGetDimensionsSignature: string | null = null;
  private lastGetDimensionsAt = 0;
  private lastClickSeq = 0;
  private lastRecoveryAttemptAt = 0;
  private isRecoveringElement = false;
  private options = {
    onBeforeFind: () => {
      this.stopPositionMonitoring();
    },
    onElementFound: (element: HTMLElement) => {
      this.currentElement = element;
      this.startPositionMonitoring();
    },
    onElementLost: () => {
      Container.getInstance().hide();
      Overlay.getInstance().hide();
      ClickCapture.getInstance().hide();
    }
  };

  constructor() {
    this.startListeners();
    ClickCapture.init(this.clickEventHandler);
  }

  private startListeners() {
    Container.getInstance().sendMessage({
      type: MessageTypes.RUNNING_GUIDE
    });

    this.messageListener = async (event: MessageEvent) => {
      if (event.data.type === MessageTypes.CHECK_RUNNING_GUIDE) {
        if (this.paths) {
          const { frameElement } = Finder.splitPathsByIframe(this.paths);
          if (frameElement && event.source === frameElement.contentWindow) {
            await this.getElementDimensions(event, this.paths);
          }
        }
      } else if (event.data.type === MessageTypes.GET_ELEMENT_DIMENSIONS) {
        await this.getElementDimensions(event);
      } else if (event.data.type === MessageTypes.RESIZE_AND_POSITION_IFRAME) {
        if (event.data.show) {
          this.stopPositionMonitoring();
          this.currentElement = null;
          this.paths = [];
        }
        const bounds = this.currentElement?.getBoundingClientRect();
        if ((bounds && (bounds.width > 0 || bounds.height > 0)) || event.data.show) {
          Container.getInstance().resizeAndPosition(
            event.data.position,
            event.data.dimensions,
            event.data.show
          );
        }
      } else if (event.data.type === MessageTypes.POSITION_CHANGED) {
        this.sendElementDimensions();
      }
      if (event.data.type === MessageTypes.ELEMENT_LOST) {
        Container.getInstance().hide();
        Overlay.getInstance().hide();
        ClickCapture.getInstance().hide();
      }
      if (event.data.type === MessageTypes.GO_TO_NEXT_STEP) {
        Container.getInstance().sendMessage({
          type: MessageTypes.GO_TO_NEXT_STEP,
          nextStepButtonType: this.nextStepButtonType
        });
      }
      if (event.data.type === MessageTypes.GO_TO_NEXT_STEP_SUCCESS) {
        const { frameElement } = Finder.splitPathsByIframe(this.paths);
        printGuideLog({
          message: '[Guide] GO_TO_NEXT_STEP_SUCCESS',
          data: {
            seq: this.lastClickSeq,
            hasFrame: Boolean(frameElement),
            shouldDestroy: Boolean(event.data.shouldDestroy),
            willDispatchClick: !frameElement
          }
        });
        if (frameElement) {
          frameElement.contentWindow?.postMessage(
            {
              type: MessageTypes.GO_TO_NEXT_STEP_SUCCESS
            },
            '*'
          );
        } else {
          ClickCapture.getInstance().dispatchClick(this.clickedElement as HTMLElement);
        }
        if (event.data.shouldDestroy) {
          window.postMessage(
            {
              type: MessageTypes.DESTROY
            },
            '*'
          );
        }
      }
    };

    window.addEventListener('message', this.messageListener);
  }

  public destroy(): void {
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = null;
    }
    ClickCapture.getInstance().destroy();
    this.stopPositionMonitoring();
    // Invalidate any getElementDimensions still in flight: it awaits layout stability and the
    // Finder resolution, so its continuation can land AFTER teardown and would then scroll the
    // host page once the guide has already stopped.
    this.scrollRequestToken += 1;
    this.dimensionsApplyToken += 1;
    this.currentElement = null;
    this.paths = [];
    if (this.scrollReleaseTimeoutId) {
      window.clearTimeout(this.scrollReleaseTimeoutId);
      this.scrollReleaseTimeoutId = null;
    }
  }

  private buildPathsSignature = (
    paths: {
      xpath: string;
      cssPath: string[];
      textXPath: string | null;
      shadowRoot?: boolean;
      iframe?: boolean;
    }[]
  ) => {
    return paths
      .map(path => `${path.xpath}|${path.cssPath.join('>')}|${path.textXPath || ''}`)
      .join('||');
  };

  private buildScrollSignature = (
    dimensions: { x: number; y: number; width: number; height: number },
    paths: {
      xpath: string;
      cssPath: string[];
      textXPath: string | null;
      shadowRoot?: boolean;
      iframe?: boolean;
    }[]
  ) => {
    const roundedX = Math.round(dimensions.x / 10);
    const roundedY = Math.round(dimensions.y / 10);
    const roundedW = Math.round(dimensions.width / 10);
    const roundedH = Math.round(dimensions.height / 10);
    return `${this.buildPathsSignature(paths)}::${roundedX}:${roundedY}:${roundedW}:${roundedH}`;
  };

  private waitForLayoutStability = async (element: HTMLElement) => {
    const start = Date.now();
    const maxWaitMs = 200;
    const checkIntervalMs = 50;
    let stableChecks = 0;
    let lastY = Math.round(element.getBoundingClientRect().y);
    let lastScrollHeight = document.documentElement.scrollHeight;

    while (Date.now() - start < maxWaitMs) {
      await new Promise(resolve => window.setTimeout(resolve, checkIntervalMs));
      if (!element.isConnected) return;

      const currentY = Math.round(element.getBoundingClientRect().y);
      const currentScrollHeight = document.documentElement.scrollHeight;
      const yDelta = Math.abs(currentY - lastY);
      const scrollHeightDelta = Math.abs(currentScrollHeight - lastScrollHeight);

      if (yDelta <= 2 && scrollHeightDelta <= 2) {
        stableChecks += 1;
        if (stableChecks >= 2) return;
      } else {
        stableChecks = 0;
      }

      lastY = currentY;
      lastScrollHeight = currentScrollHeight;
    }
  };

  /**
   * Waits until the target's viewport rect stops moving after scrollIntoView
   * (smooth animations), so chrome is painted with the final coordinates.
   */
  private waitForElementScrollSettle = async (element: HTMLElement, requestToken: number) => {
    const start = Date.now();
    const maxWaitMs = 600;
    const checkIntervalMs = 50;
    let stableChecks = 0;
    let lastX = Math.round(element.getBoundingClientRect().x);
    let lastY = Math.round(element.getBoundingClientRect().y);

    while (Date.now() - start < maxWaitMs) {
      await new Promise(resolve => window.setTimeout(resolve, checkIntervalMs));
      if (requestToken !== this.scrollRequestToken || !element.isConnected) return;

      const rect = element.getBoundingClientRect();
      const currentX = Math.round(rect.x);
      const currentY = Math.round(rect.y);

      if (Math.abs(currentX - lastX) <= 2 && Math.abs(currentY - lastY) <= 2) {
        stableChecks += 1;
        if (stableChecks >= 2) return;
      } else {
        stableChecks = 0;
      }

      lastX = currentX;
      lastY = currentY;
    }
  };

  private releaseScrollLockLater = () => {
    if (this.scrollReleaseTimeoutId) {
      window.clearTimeout(this.scrollReleaseTimeoutId);
    }
    this.scrollReleaseTimeoutId = window.setTimeout(() => {
      this.scrollLock = false;
      this.scrollReleaseTimeoutId = null;
    }, 500);
  };

  private requestScrollToCurrentElement = (
    dimensions: {
      x: number;
      y: number;
      width: number;
      height: number;
      frameElement?: HTMLIFrameElement;
    },
    paths: {
      xpath: string;
      cssPath: string[];
      textXPath: string | null;
      shadowRoot?: boolean;
      iframe?: boolean;
    }[],
    behavior: ScrollBehavior = 'smooth'
  ) => {
    if (!this.currentElement) return false;

    const signature = this.buildScrollSignature(dimensions, paths);
    const now = Date.now();
    const isDuplicateRequest =
      this.lastScrollSignature === signature && now - this.lastScrollAt < 250;

    if (this.scrollLock || isDuplicateRequest) {
      return false;
    }

    this.scrollLock = true;
    this.lastScrollSignature = signature;
    this.lastScrollAt = now;

    scrollElementIntoViewForGuide(this.currentElement, behavior);

    if (dimensions.frameElement) {
      dimensions.frameElement.contentWindow?.postMessage(
        {
          type: MessageTypes.SCROLL_TO_ELEMENT
        },
        '*'
      );
    }

    this.releaseScrollLockLater();
    return true;
  };

  private getElementDimensions = async (
    event: MessageEvent,
    paths?: {
      xpath: string;
      cssPath: string[];
      textXPath: string | null;
      shadowRoot?: boolean;
      iframe?: boolean;
    }[]
  ) => {
    const pathsToUse = (
      event.data.customPath
        ? [{ xpath: '', cssPath: [event.data.customPath], textXPath: null }]
        : event.data.paths || paths
    ) as {
      xpath: string;
      cssPath: string[];
      textXPath: string | null;
      shadowRoot?: boolean;
      iframe?: boolean;
    }[];

    const pathsSignature = this.buildPathsSignature(pathsToUse);
    const now = Date.now();
    if (this.lastGetDimensionsSignature === pathsSignature && now - this.lastGetDimensionsAt < 50) {
      return;
    }
    this.lastGetDimensionsSignature = pathsSignature;
    this.lastGetDimensionsAt = now;
    this.scrollRequestToken += 1;
    this.dimensionsApplyToken += 1;
    const currentRequestToken = this.scrollRequestToken;

    this.stopPositionMonitoring();
    Container.getInstance().hide();
    Overlay.getInstance().hide();
    ClickCapture.getInstance().hide();

    const shouldShowHighlight = Boolean(event.data.highlight);
    const highlightColor = event.data.highlightColor;

    if (shouldShowHighlight && highlightColor) {
      Overlay.getInstance().setBorderColor(highlightColor);
    }

    this.shouldDimBackground = Boolean(event.data.dimBackground);
    this.shouldShowHighlight = shouldShowHighlight;
    this.nextStepButtonType = event.data.nextStepButtonType;

    this.paths = pathsToUse;

    printGuideLog({
      message: '[Guide] getElementDimensions:resolvePaths',
      data: {
        customPath: Boolean(event.data.customPath),
        pathSummary: summarizePathsForLog(pathsToUse),
        highlight: shouldShowHighlight,
        dimBackground: this.shouldDimBackground
      }
    });

    const dimensions = await Finder.getOverallElementDimensionsByPath(
      pathsToUse,
      this.options,
      this.nextStepButtonType
    );

    if (currentRequestToken !== this.scrollRequestToken) {
      printGuideLog({
        message: '[Guide] getElementDimensions:staleRequestDropped',
        data: { currentRequestToken, scrollRequestToken: this.scrollRequestToken }
      });
      return;
    }

    printGuideLog({
      message: '[Guide] getElementDimensions:afterResolve',
      data: {
        isElementVisible: dimensions.isElementVisible,
        isOutOfViewport: dimensions.isOutOfViewport,
        hasCurrentElement: Boolean(this.currentElement),
        rect: {
          x: dimensions.x,
          y: dimensions.y,
          width: dimensions.width,
          height: dimensions.height
        },
        willShowChrome: dimensions.isElementVisible
      }
    });

    let paintDimensions = dimensions;

    // The scroll can only honour the margin the scrollport has room for, so the comfort
    // threshold has to be measured with the same number -- otherwise a target inside a
    // short panel reads as uncomfortable forever and pays a second scroll round for nothing.
    const comfortMargin = this.currentElement
      ? resolveGuideScrollMargin(this.currentElement)
      : GUIDE_SCROLL_MARGIN_PX;

    const needsComfortScroll =
      dimensions.isOutOfViewport ||
      (!dimensions.isElementVisible && this.currentElement) ||
      (dimensions.isElementVisible &&
        isGuideTargetUncomfortable(dimensions, undefined, comfortMargin));

    if (needsComfortScroll && this.currentElement) {
      // Pause tracking so mid-scroll measures cannot paint the tooltip before we settle.
      this.dimensionsApplyToken += 1;
      this.stopPositionMonitoring();

      try {
        await this.waitForLayoutStability(this.currentElement);
        if (currentRequestToken !== this.scrollRequestToken || !this.currentElement) return;

        const scrolled = this.requestScrollToCurrentElement(dimensions, pathsToUse, 'smooth');
        await this.waitForElementScrollSettle(this.currentElement, currentRequestToken);
        if (currentRequestToken !== this.scrollRequestToken || !this.currentElement) return;

        let updatedDimensions = await Finder.getOverallElementDimensionsByElement(
          this.currentElement,
          this.paths
        );
        if (!updatedDimensions) return;

        if (
          updatedDimensions.isOutOfViewport ||
          !updatedDimensions.isElementVisible ||
          isGuideTargetUncomfortable(updatedDimensions, undefined, comfortMargin)
        ) {
          this.scrollLock = false;
          const rescrolled = this.requestScrollToCurrentElement(
            updatedDimensions,
            this.paths,
            'auto'
          );
          // A dropped request (same signature, nothing moved) means the page cannot scroll
          // any further -- waiting for a scroll that never starts only delays the tooltip.
          if (rescrolled) {
            await this.waitForElementScrollSettle(this.currentElement, currentRequestToken);
            if (currentRequestToken !== this.scrollRequestToken || !this.currentElement) return;

            updatedDimensions = await Finder.getOverallElementDimensionsByElement(
              this.currentElement,
              this.paths
            );
            if (!updatedDimensions) return;
          }
        }

        if (currentRequestToken !== this.scrollRequestToken) return;
        printGuideLog({
          message: '[Guide] getElementDimensions:afterComfortScroll',
          data: {
            scrolled,
            comfortMargin,
            rect: {
              x: updatedDimensions.x,
              y: updatedDimensions.y,
              width: updatedDimensions.width,
              height: updatedDimensions.height
            },
            isElementVisible: updatedDimensions.isElementVisible,
            isOutOfViewport: updatedDimensions.isOutOfViewport
          }
        });
        paintDimensions = updatedDimensions;
      } finally {
        // Tracking is the only thing that re-shows the chrome once the visitor scrolls back,
        // and the only thing that recovers a re-rendered target. Every exit path above has to
        // leave it armed, or an aborted comfort scroll freezes the guide with no tooltip.
        if (currentRequestToken === this.scrollRequestToken) {
          this.startPositionMonitoring();
        }
      }
    }

    if (paintDimensions.isElementVisible) {
      printGuideLog({ message: '[Guide] getElementDimensions:showingChrome' });
      Container.getInstance().show();

      if (shouldShowHighlight && highlightColor) {
        Overlay.getInstance().show(
          {
            x: paintDimensions.x,
            y: paintDimensions.y,
            width: paintDimensions.width,
            height: paintDimensions.height
          },
          this.shouldDimBackground,
          shouldShowHighlight
        );
      }

      Container.getInstance().sendMessage({
        type: MessageTypes.SET_ELEMENT_DIMENSIONS,
        dimensions: {
          x: paintDimensions.x,
          y: paintDimensions.y,
          width: paintDimensions.width,
          height: paintDimensions.height,
          containerWidth: paintDimensions.containerWidth,
          containerHeight: paintDimensions.containerHeight
        }
      });
    } else {
      printGuideLog({
        message: '[Guide] getElementDimensions:chromeHidden',
        data: {
          isElementVisible: paintDimensions.isElementVisible,
          isOutOfViewport: paintDimensions.isOutOfViewport,
          hasCurrentElement: Boolean(this.currentElement)
        },
        level: 'warn'
      });
    }
  };

  private clickEventHandler = (e: MouseEvent) => {
    this.lastClickSeq += 1;
    printGuideLog({
      message: '[Guide] clickEventHandler',
      data: {
        seq: this.lastClickSeq,
        clientX: e.clientX,
        clientY: e.clientY,
        nextStepButtonType: this.nextStepButtonType
      }
    });
    this.clickedElement = Finder.deepElementFromPoint(
      document as DocumentOrShadowRoot,
      e.clientX,
      e.clientY
    ) as HTMLElement;
    Container.getInstance().sendMessage({
      type: MessageTypes.GO_TO_NEXT_STEP,
      nextStepButtonType: this.nextStepButtonType
    });
  };

  private stopPositionMonitoring = () => {
    if (this.positionAnimationId) {
      cancelAnimationFrame(this.positionAnimationId);
      this.positionAnimationId = null;
    }
    this.lastPosition = null;
    this.lastVisibility = null;
    this.lastRecoveryAttemptAt = 0;
    this.isRecoveringElement = false;
  };

  private tryRecoverLostElement = (currentTime: number) => {
    if (!this.isRecoveringElement) {
      this.isRecoveringElement = true;
      this.options.onElementLost();
      printGuideLog({
        message: '[Guide] positionMonitor:elementLost',
        data: { pathSummary: summarizePathsForLog(this.paths) },
        level: 'warn'
      });
    }

    if (isGuideRecoveryDisabled()) return;
    if (currentTime - this.lastRecoveryAttemptAt < Guide.RECOVERY_THROTTLE_MS) return;
    this.lastRecoveryAttemptAt = currentTime;
    if (!this.paths.length) return;

    const { mainPaths } = Finder.splitPathsByIframe(this.paths);
    const found = Finder.findElementByPaths(mainPaths) as HTMLElement | null;
    if (!found || !found.isConnected) return;

    this.currentElement = found;
    this.isRecoveringElement = false;
    this.lastPosition = null;
    this.lastVisibility = null;
    printGuideLog({
      message: '[Guide] positionMonitor:recoveredElement',
      data: { tag: found.tagName, id: found.id }
    });
    this.sendElementDimensions();
  };

  private startPositionMonitoring = () => {
    this.stopPositionMonitoring();

    let lastCheckTime = 0;
    const THROTTLE_MS = 25;
    const POSITION_THRESHOLD = 2;

    const checkPosition = (currentTime: number) => {
      if (!this.currentElement || !this.currentElement.isConnected) {
        this.tryRecoverLostElement(currentTime);
        this.positionAnimationId = requestAnimationFrame(checkPosition);
        return;
      }

      if (currentTime - lastCheckTime < THROTTLE_MS) {
        this.positionAnimationId = requestAnimationFrame(checkPosition);
        return;
      }

      lastCheckTime = currentTime;

      const rect = this.currentElement.getBoundingClientRect();
      const currentPosition = {
        x: rect.x,
        y: rect.y,
        width: rect.width,
        height: rect.height
      };

      const currentVisibility = Finder.isElementVisible(this.currentElement);

      const positionChanged =
        this.lastPosition &&
        (Math.abs(currentPosition.x - this.lastPosition.x) > POSITION_THRESHOLD ||
          Math.abs(currentPosition.y - this.lastPosition.y) > POSITION_THRESHOLD ||
          Math.abs(currentPosition.width - this.lastPosition.width) > POSITION_THRESHOLD ||
          Math.abs(currentPosition.height - this.lastPosition.height) > POSITION_THRESHOLD);

      const visibilityChanged = this.lastVisibility !== currentVisibility;

      if (positionChanged || visibilityChanged) {
        this.sendElementDimensions();
      }

      this.lastPosition = currentPosition;
      this.lastVisibility = currentVisibility;
      this.positionAnimationId = requestAnimationFrame(checkPosition);
    };

    this.positionAnimationId = requestAnimationFrame(checkPosition);
  };

  private sendElementDimensions = async () => {
    if (!this.currentElement || !this.currentElement.isConnected) {
      Container.getInstance().hide();
      Overlay.getInstance().hide();
      ClickCapture.getInstance().hide();
      return;
    }

    const applyToken = ++this.dimensionsApplyToken;
    const targetElement = this.currentElement;

    const dimensions = await Finder.getOverallElementDimensionsByElement(targetElement, this.paths);

    if (applyToken !== this.dimensionsApplyToken) {
      printGuideLog({
        message: '[Guide] sendElementDimensions:staleApplyDropped',
        data: { applyToken, dimensionsApplyToken: this.dimensionsApplyToken }
      });
      return;
    }

    if (!dimensions) return;

    if (dimensions.isOutOfViewport || !dimensions.isElementVisible) {
      printGuideLog({
        message: '[Guide] sendElementDimensions:hideWhileTracking',
        data: {
          isOutOfViewport: dimensions.isOutOfViewport,
          isElementVisible: dimensions.isElementVisible,
          scrollLock: this.scrollLock
        }
      });
      // Hide chrome while the user scrolls away — do not force-scroll them back.
      // Auto-scroll only runs on step entry (getElementDimensions comfort path).
      Container.getInstance().hide();
      Overlay.getInstance().hide();
      ClickCapture.getInstance().hide();
      return;
    }

    Container.getInstance().show();

    if (this.nextStepButtonType === NextStepButtonType.ELEMENT) {
      ClickCapture.getInstance().show(this.paths);
    }

    if (this.shouldDimBackground || this.shouldShowHighlight) {
      Overlay.getInstance().show(
        {
          x: dimensions.x,
          y: dimensions.y,
          width: dimensions.width,
          height: dimensions.height
        },
        this.shouldDimBackground,
        this.shouldShowHighlight
      );
    }

    Container.getInstance().sendMessage({
      type: MessageTypes.SET_ELEMENT_DIMENSIONS,
      dimensions: {
        x: dimensions.x,
        y: dimensions.y,
        width: dimensions.width,
        height: dimensions.height,
        containerWidth: dimensions.containerWidth,
        containerHeight: dimensions.containerHeight
      }
    });
  };
}

export default Guide;
